import { CdkCellDef, CdkTableDataSourceInput } from '@angular/cdk/table';
import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { MatCellDef } from '@angular/material/table';

export interface RsCellContext<T> {
  $implicit: T;
  index: number;
}

/**
 * @directive RsTypeSafeMatCellDef
 * @selector [rsMatCellDef]
 * @standalone true
 * @description
 *   This directive provides a type-safe implementation for defining the content of a material table cell.
 *   It extends the `MatCellDef` directive and ensures that the data source input and the template
 *   context are correctly typed.
 *
 * @usageNotes
 *   - Apply this directive to an `mat-cell` element within an `rs-table` component.
 *   - Bind the `rsMatCellDefDataSource` input to the data source providing the cell values.
 *   - Use the template to render the cell content, accessing the data item via `$implicit` and the
 *     index via `index`.
 *
 * @example
 *   ```html
 *   <rs-table [dataSource]="myDataSource">
 *     <mat-cell *rsMatCellDef [rsMatCellDefDataSource]="myDataSource">
 *       <td>{{ $implicit.propertyName }}</td>
 *     </mat-cell>
 *   </rs-table>
 *   ```
 *
 * @typeParam T The type of data items in the table's data source.
 */
@Directive({
  selector: '[rsMatCellDef]',
  providers: [{ provide: CdkCellDef, useExisting: RsTypeSafeMatCellDef }]
})
export class RsTypeSafeMatCellDef<T> extends MatCellDef {
  @Input({ required: true }) public rsMatCellDefDataSource!: CdkTableDataSourceInput<T>;

  constructor(
    template: TemplateRef<RsCellContext<T>>,
    viewContainer: ViewContainerRef
  ) {
    super(template, viewContainer);
  }

  // ngTemplateContextGuard flag to help with the Language Service
  public static ngTemplateContextGuard<T>(
    directive: RsTypeSafeMatCellDef<T>,
    context: unknown
  ): context is RsCellContext<T> {
    return true;
  }
}
